import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { FetchError } from 'ofetch'
import type {
  AddItemsToCartRequest,
  UpdateBundleItemsRequest,
  CartResponseSuccess,
  ShippingInformationRequest,
} from '@/types/cart'
import { regularItemsBundleMap } from '@/utils/normalize/cartServiceNormalize'
import { useGuestStore } from '~/store/guest'
import { useGuestCartRepository } from '~/composables/api/repositories/guest-cart'
import { piniaPluginPersistedstate, useState, storeToRefs } from '#imports'

const CATaxSku = 'CA Rx Handling Fee'

export const useGuestCartStore = defineStore(
  'guestCart',
  () => {
    const cartId = ref<string | null>(null)
    const cartStoreId = ref<string | null>(null)
    const cart = ref<CartResponseSuccess | null>(null)
    const cartApi = useGuestCartRepository()
    const { currentGift } = storeToRefs(useGuestStore())
    const requestError = ref<FetchError | null>(null)
    const loading = useState<boolean>('guestCartStoreLoading', () => false)

    const prices = computed(() => cart.value?.prices)
    const itemCount = computed<number>(() => {
      const regularBundleMaps = regularItemsBundleMap(cart.value?.bundle_map)
      return Object.keys(regularBundleMaps ?? {}).length
    })

    const createCart = async () => {
      loading.value = true
      try {
        const response = await cartApi.execute<'init'>(cartApi.init())
        if (response) {
          cart.value = response
          cartId.value = response.id
          cartStoreId.value = currentGift.value?.storeId ?? ''
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const loadCart = async (id?: string) => {
      loading.value = true
      try {
        const response = await cartApi.execute<'get'>(
          cartApi.get((id || cartId.value) ?? ''),
        )
        if (response) {
          cart.value = response
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          if (fetchError.statusCode === 403) {
            await createCart()
          }
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const initCart = async () => {
      if (cartId.value && cart.value) {
        return
      }

      if (!cartId.value) {
        await createCart()
      } else if (!cart.value) {
        await loadCart()
      }
    }

    const addItems = async (items: AddItemsToCartRequest) => {
      loading.value = true
      try {
        const response = await cartApi.execute<'addItems'>(
          cartApi.addItems(cart.value!.id, items),
        )
        if (response) {
          cart.value = response
          await updatePromoCode()
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const removeBundle = async (bundleId: string) => {
      loading.value = true
      try {
        const response = await cartApi.execute<'removeBundle'>(
          cartApi.removeBundle(cart.value!.id, bundleId),
        )
        if (response) {
          cart.value = response
          if (
            isShippingToCA.value &&
            !cartHasRxItems.value &&
            caTaxItem.value
          ) {
            await removeCATax()
          }
          await updatePromoCode()
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const updateBundle = async (
      bundleId: string,
      params: UpdateBundleItemsRequest,
    ) => {
      loading.value = true
      try {
        const response = await cartApi.execute<'updateBundle'>(
          cartApi.updateBundle(cart.value!.id, bundleId, params),
        )
        if (response) {
          cart.value = response
          if (isShippingToCA.value) {
            if (cartHasRxItems.value && !cartHasCATax.value) {
              await addCATax()
            } else if (!cartHasRxItems.value && cartHasCATax.value) {
              await removeCATax()
            }
          }
          await updatePromoCode()
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const addShipping = async (params: ShippingInformationRequest) => {
      loading.value = true
      try {
        const response = await cartApi.execute<'addShipping'>(
          cartApi.addShipping(cart.value!.id, params),
        )
        if (response) {
          cart.value = response.cart
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const reset = () => {
      cartId.value = null
      cart.value = null
      cartStoreId.value = null
    }

    const addCATax = async () => {
      if (!cart.value) {
        return
      }
      if (cartHasCATax.value) {
        return
      }
      return await addItems({
        cart_items: [
          {
            sku: CATaxSku,
            quantity: 1,
          },
        ],
        isRegularItem: false,
        isPrescription: false,
        prescription: {
          pd: 0,
          lensType: 'nonprescription',
          isOlderThan12: false,
          od: {
            sphere: 0,
            cylinders: 0,
            axis: 0,
          },
          os: {
            sphere: 0,
            cylinders: 0,
            axis: 0,
          },
        },
      })
    }

    const findBundleWithCaTax = (
      data: CartResponseSuccess['bundle_map'],
    ): string | undefined => {
      for (const [key, value] of Object.entries(data)) {
        if (
          !value.isRegularItem &&
          value.items.some((item) => item.sku === CATaxSku)
        ) {
          return key
        }
      }
      return undefined
    }

    const removeCATax = async () => {
      if (!cart.value) {
        return
      }
      const taxBundle = findBundleWithCaTax(cart.value.bundle_map)
      if (!taxBundle) {
        return
      }
      return await removeBundle(taxBundle)
    }

    const updateOcrRequestId = async (
      ocrRequestId: string | null,
      bundleId?: string,
    ) => {
      loading.value = true
      try {
        const firstPrescriptionBundleWithoutOcr = Object.entries(
          cart.value!.bundle_map,
        )
          .filter(([_, bundle]) => bundle.isPrescription)
          .find(([_, bundle]) => !bundle.prescription.ocrRequestId)

        const bundleToUpdate =
          bundleId ?? firstPrescriptionBundleWithoutOcr?.[0] ?? ''
        const response = await cartApi.execute<'updateOcrRequestId'>(
          cartApi.updateOcrRequestId(
            cart.value!.id,
            bundleToUpdate,
            ocrRequestId,
          ),
        )
        if (response) {
          cart.value = response
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      } finally {
        loading.value = false
      }
    }

    const isShippingToCA = computed(() => {
      if (!cart.value?.shipping_addresses?.length) {
        return false
      }
      return (
        (cart.value?.shipping_addresses?.[0]?.country?.code.toLowerCase() ===
          'us' &&
          cart.value?.shipping_addresses?.[0]?.region?.code.toLowerCase() ===
            'ca') ??
        false
      )
    })
    const caTaxItem = computed(() => {
      if (!cart.value?.items) {
        return undefined
      }
      return cart.value.items.find((item) => item.product.sku === CATaxSku)
    })
    const cartHasRxItems = computed(() => {
      if (!cart.value?.bundle_map) {
        return false
      }
      return Object.values(cart.value.bundle_map).some(
        (bundle) => bundle.isPrescription,
      )
    })
    const allItemsHasOcr = computed(() => {
      if (!cart.value?.bundle_map) {
        return false
      }
      return Object.values(cart.value.bundle_map)
        .filter((bundle) => bundle.isPrescription)
        .every((bundle) => bundle.prescription.ocrRequestId)
    })
    const cartHasCATax = computed(() => {
      if (!cart.value?.bundle_map) {
        return false
      }
      return Object.values(cart.value.bundle_map).some(
        (bundle) =>
          !bundle.isRegularItem &&
          bundle.items.some((item) => item.sku === CATaxSku),
      )
    })

    const addPromoCode = async (promoCode: string) => {
      if (!cart.value?.id) return

      try {
        const response = await cartApi.execute<'addPromotion'>(
          cartApi.addPromotion(cart.value.id, promoCode),
        )
        if (response) {
          cart.value = response
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      }
    }

    const removePromoCode = async () => {
      if (!cart.value?.id) return

      try {
        const response = await cartApi.execute<'removePromotion'>(
          cartApi.removePromotion(cart.value.id),
        )
        if (response) {
          cart.value = response
        }
      } catch (error) {
        const fetchError = cartApi.getError(error)
        if (fetchError) {
          console.error(fetchError)
          requestError.value = fetchError
        }
      }
    }

    const updatePromoCode = async () => {
      if (!currentGift.value?.promotionCode || !cart.value?.total_quantity) {
        return
      }

      const hasDiscount = !!currentGift.value?.discountPercentage
      const hasOrderMin = !!currentGift.value?.orderMinimum
      const shouldAlwaysApplyPromo = hasDiscount && !hasOrderMin
      const subtotal =
        cart.value?.prices?.subtotal_excluding_tax?.value ?? undefined
      const hasReachedDiscountMinimum =
        hasOrderMin &&
        subtotal &&
        Number(subtotal) >= Number(currentGift.value?.orderMinimum)
      const shouldApplyPromo =
        shouldAlwaysApplyPromo || hasReachedDiscountMinimum
      const hasPromoCode = (cart.value?.applied_coupons?.length ?? 0) > 0

      console.info('[updatePromoCode] Debug:', {
        hasDiscount,
        hasOrderMin,
        shouldAlwaysApplyPromo,
        subtotal,
        hasReachedDiscountMinimum,
        shouldApplyPromo,
        hasPromoCode,
        promotionCode: currentGift.value?.promotionCode,
      })

      if (
        shouldApplyPromo &&
        currentGift.value?.promotionCode &&
        !hasPromoCode
      ) {
        await addPromoCode(currentGift.value?.promotionCode)
      } else if (!shouldApplyPromo && hasPromoCode) {
        await removePromoCode()
      }
    }

    return {
      cart,
      cartId,
      cartStoreId,
      prices,
      itemCount,
      loading,
      isShippingToCA,
      cartHasRxItems,
      allItemsHasOcr,
      cartHasCATax,
      caTaxItem,
      createCart,
      loadCart,
      initCart,
      addItems,
      removeBundle,
      updateBundle,
      addShipping,
      reset,
      addCATax,
      removeCATax,
      updateOcrRequestId,
    }
  },
  {
    persist: [
      {
        storage: piniaPluginPersistedstate.cookies({
          sameSite: 'strict',
        }),
        pick: ['cartId', 'cartStoreId'],
      },
    ],
  },
)
